import React from 'react';
import { Card } from '../../ui/Card';
import { useWeight } from '../../../context/WeightContext';
import { useAuth } from '../../../context/AuthContext';
import { sb } from '../../../modules/state';

export function WeightDashboard() {
  const { goalWeight, startWeight, entries, showHistoricalEntries, setShowHistoricalEntries, formatWeight: formatWeightHelper } = useWeight();

  // Calculate which entries to show based on toggle
  const filteredEntries = React.useMemo(() => {
    if (showHistoricalEntries) return entries;

    // Show all entries - dashboard should show all data
    return entries;
  }, [entries, showHistoricalEntries]);

  // Current weight = most recent entry by recorded_at date
  const lastEntry = filteredEntries[0];
  const currentWeight = lastEntry?.weight_kg || null;
  const lastEntryDate = lastEntry?.recorded_at ? new Date(lastEntry.recorded_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : 'N/A';

  // Total Lost = start_weight_kg - last (most recent) weight entry
  const totalLost = startWeight && currentWeight ? startWeight - currentWeight : 0;

  // Weight to go = current_weight - goal_weight (how much more to lose)
  const weightToGo = goalWeight && currentWeight ? currentWeight - goalWeight : 0;

  // % Weight Lost = (current_weight - goal_weight) / (start_weight - goal_weight) * 100
  // This represents % of total weight loss journey completed
  const progressPercent = startWeight && goalWeight && currentWeight && (startWeight - goalWeight) > 0
    ? Math.min(100, Math.round(((startWeight - currentWeight) / (startWeight - goalWeight)) * 100))
    : 0;

  // Trajectory chart data (last 10 entries)
  const chartData = filteredEntries.slice(0, 10).reverse();
  const minWeight = chartData.length > 0 ? Math.min(...chartData.map(e => e.weight_kg)) - 1 : 50;
  const maxWeight = chartData.length > 0 ? Math.max(...chartData.map(e => e.weight_kg)) + 1 : 200;
  const range = maxWeight - minWeight || 1;

  const chartPoints = chartData.length > 1 ? chartData.map((entry, i) => {
    const x = (i / (chartData.length - 1)) * 100;
    const y = 100 - ((entry.weight_kg - minWeight) / range) * 80 - 10;
    return `${x},${y}`;
  }).join(' ') : '';

  return (
    <div className="space-y-3 p-3">
      {/* Trajectory Chart - Moved from Trajectory tab */}
      <div className="px-1">
        <Card className="h-48 flex flex-col items-center justify-center bg-gradient-to-br from-surface-container-high to-surface border border-outline">
          <div className="w-full px-3">
            <svg className="w-full h-40" viewBox="0 0 100 100" preserveAspectRatio="none">
              <defs>
                <linearGradient id="dashboardGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor="rgb(255, 233, 176)" stopOpacity="0.5" />
                  <stop offset="100%" stopColor="rgb(255, 233, 176)" stopOpacity="0" />
                </linearGradient>
              </defs>
              {chartData.length > 1 ? (
                <>
                  {/* Area fill */}
                  <polygon points={`0,100 ${chartPoints} 100,100`} fill="url(#dashboardGradient)" />
                  {/* Line */}
                  <polyline
                    points={chartPoints}
                    fill="none"
                    stroke="#ffe9b0"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  {/* Points */}
                  {chartData.map((entry, i) => {
                    const x = (i / (chartData.length - 1)) * 100;
                    const y = 100 - ((entry.weight_kg - minWeight) / range) * 80 - 10;
                    return (
                      <circle key={i} cx={x} cy={y} r="1.5" fill="#ffe9b0" className="hover:r-2" />
                    );
                  })}
                </>
              ) : (
                <text x="50" y="50" textAnchor="middle" className="text-[8px] fill-on-surface-variant">No data yet</text>
              )}
            </svg>
            {/* Y-axis labels */}
            {chartData.length > 0 && (
              <div className="flex justify-between text-[10px] text-on-surface-variant mt-2">
                <span>{maxWeight.toFixed(0)}</span>
                <span>{((maxWeight + minWeight) / 2).toFixed(0)}</span>
                <span>{minWeight.toFixed(0)}</span>
              </div>
            )}
          </div>
        </Card>
      </div>

      <div className="flex items-center justify-between px-2 py-2">
        <h2 className="text-lg font-bold text-on-surface">Weight Dashboard</h2>
        <div className="flex items-center gap-2">
          <span className="rounded-full bg-primary/20 px-3 py-1 text-xs font-medium text-primary">Streak: 45 days</span>
        </div>
      </div>

      {/* View Toggle */}
      <div className="px-1">
        <Card className="flex items-center justify-between p-2 bg-surface-container border border-outline">
          <div className="flex items-center gap-2">
            <span className="material-symbols-outlined text-sm text-on-surface-variant">history</span>
            <span className="text-sm text-on-surface">Show Historical Data</span>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={showHistoricalEntries}
              onChange={(e) => setShowHistoricalEntries(e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-surface rounded-full peer peer-focus:ring-2 peer-focus:ring-primary peer-checked:bg-primary transition-colors">
              <div className="absolute left-1 top-1 w-4 h-4 bg-surface rounded-full shadow transition-transform peer-checked:translate-x-5" />
            </div>
          </label>
        </Card>
      </div>

      <div className="px-1">
        <Card className="flex flex-col items-center justify-center py-6 bg-gradient-to-br from-surface-container-high to-surface border border-outline">
          <span className="mb-1 text-sm text-on-surface-variant">Current Weight</span>
          <span className="text-5xl font-bold text-primary">{currentWeight ? formatWeightHelper(currentWeight) : '--'}</span>
          <span className="text-xs text-on-surface-variant">{currentWeight ? `Last: ${formatWeightHelper(currentWeight)} (${lastEntryDate})` : 'No entries yet'}</span>
        </Card>
      </div>

      <div className="grid grid-cols-2 gap-2 px-1">
        <Card className="flex flex-col items-center py-3 bg-surface-container border border-outline">
          <span className="text-[10px] text-on-surface-variant">Start</span>
          <span className="text-base font-bold text-on-surface">{startWeight ? formatWeightHelper(startWeight) : '--'}</span>
          <span className="text-[10px] text-on-surface-variant">kg</span>
        </Card>
        <Card className="flex flex-col items-center py-3 bg-surface-container border border-outline">
          <span className="text-[10px] text-on-surface-variant">Goal</span>
          <span className="text-base font-bold text-on-surface">{goalWeight ? formatWeightHelper(goalWeight) : '--'}</span>
          <span className="text-[10px] text-on-surface-variant">kg</span>
        </Card>
      </div>

      <div className="grid grid-cols-3 gap-2 px-1">
        <Card className="flex flex-col items-center py-3 bg-surface-container border border-outline">
          <span className="text-[10px] text-on-surface-variant">Total Lost</span>
          <span className="text-base font-bold text-primary">{totalLost ? formatWeightHelper(totalLost) : '--'}</span>
          <span className="text-[10px] text-on-surface-variant">kg</span>
        </Card>
        <Card className="flex flex-col items-center py-3 bg-surface-container border border-outline">
          <span className="text-[10px] text-on-surface-variant">Weight to go</span>
          <span className="text-base font-bold text-primary">{weightToGo ? formatWeightHelper(weightToGo) : '--'}</span>
          <span className="text-[10px] text-on-surface-variant">kg</span>
        </Card>
        <Card className="flex flex-col items-center py-3 bg-surface-container border border-outline">
          <span className="text-[10px] text-on-surface-variant">% Weight Lost</span>
          <span className="text-base font-bold text-primary">{progressPercent}%</span>
          <span className="text-[10px] text-on-surface-variant">progress</span>
        </Card>
      </div>
    </div>
  );
}
